import BaseDialogCmp from "../BaseDialog/BaseDialogCmp.js";
import {safeText} from "../../js/util/CommonUtils.js";

export default class InjectionSearchDialogHtml {
    #dialog = new BaseDialogCmp({
        title: "Import analytes from injection",
        cssClass: "injection-search",
        btnOk: null,
        autofocusEl: ".injection-search__input"
    });

    /** @param {Object<string, function>} callbacks */
    show(callbacks) {
        this.#dialog.showHtml(`
            <main class="dialog__main-content injection-search__content">
                <div class="injection-search__search">
                    <i class="material-symbols-outlined">search</i>
                    <input class="injection-search__input" type="text" placeholder="Search injections by name">
                </div>
                <div class="injection-search__results"></div>
            </main>`);
        this.#setEventListeners(callbacks);
    }

    close() {
        this.#dialog.close();
    }

    /**
     * @param {string} query - text in the search box
     * @param {InjectionSearchRow[]} recents
     * @param {InjectionSearchRow[]} injections
     * @param {Map<string, InjectionRowState>} rowsById - has a record for every injection being drawn
     * @param {string|null} searchError - why the injections couldn't be loaded
     * @param {boolean} isLoadingList - injections are being (re)loaded, results shown are stale
     * @param {boolean} hasLoaded - is listing request has succeeded
     */
    render({query, recents, injections, rowsById, searchError, isLoadingList, hasLoaded}) {
        const listTemplate = list => `
            <div class="injection-search__list">
                ${list.map(injection => this.#rowTemplate(injection, rowsById.get(injection.id))).join('')}
            </div>`;
        const isSearching = query.trim().length > 0;
        let html = recents.length
            ? `<div class="injection-search__section">Recent</div>${listTemplate(recents)}` : '';
        const resultsTitle = searchError
            ? 'Results'
            : isSearching ? `Results (${injections.length})` : 'All injections (most recent first)';
        html += `<div class="injection-search__section">${resultsTitle}</div>`;
        if (searchError)
            html += `<div class="injection-search__empty injection-search__empty--error" role="status">
                        ${safeText(searchError)}
                        <button class="injection-search__forget" data-retry-search>retry</button>
                     </div>`;
        else if (injections.length)
            html += listTemplate(injections);
        else if (isLoadingList && !hasLoaded)
            html += `
                <div class="injection-search__empty injection-search__empty--loading" role="status">
                    <span class="injection-search__spinner" aria-hidden="true"></span> Loading injections…
                </div>`;
        else if (hasLoaded)
            html += `
                <div class="injection-search__empty">
                    ${isSearching
                ? `No injection matches “${safeText(query.trim())}”.`
                : 'There are no other injections to import analytes from.'}
                </div>`;
        const resultsEl = this.#getResultsEl();
        const focusedInjectionId = document.activeElement?.dataset?.showAnalytes;
        resultsEl.innerHTML = html;
        resultsEl.setAttribute('aria-busy', String(isLoadingList));
        resultsEl.classList.toggle('injection-search__results--busy', isLoadingList);
        resultsEl.querySelectorAll('[data-check-all]').forEach(checkAll =>
            checkAll.indeterminate = this.#isPartlySelected(rowsById.get(checkAll.dataset.checkAll)));
        if (focusedInjectionId)
            resultsEl.querySelector(`[data-show-analytes="${CSS.escape(focusedInjectionId)}"]`)?.focus();
    }

    /**
     * @param {string} injectionId
     * @param {InjectionRowState} row
     */
    redrawSelectionOf(injectionId, row) {
        const rowEl = this.#getRowEl(injectionId);
        if (!rowEl || !row.analytes)
            return;
        rowEl.querySelectorAll('[data-analyte]').forEach(checkbox =>
            checkbox.checked = row.selectedAnalyteIds.has(checkbox.dataset.analyte));
        const checkAll = rowEl.querySelector('[data-check-all]');
        if (checkAll) {
            checkAll.checked = row.selectedAnalyteIds.size === row.analytes.length;
            checkAll.indeterminate = this.#isPartlySelected(row);
        }
        const importBtn = rowEl.querySelector('[data-import]');
        if (row.importing)
            return;
        importBtn.disabled = row.selectedAnalyteIds.size === 0;
        importBtn.querySelector('[data-import-label]').textContent = this.#importLabel(row);
    }

    #setEventListeners(callbacks) {
        this.#dialog.onCancel(callbacks.onCancel);
        this.#getSearchInputEl().addEventListener('input', evt => callbacks.onSearch(evt.target.value));
        const clickActions = {
            'data-show-analytes': callbacks.onShowAnalytesClicked,
            'data-forget': callbacks.onForgetClicked,
            'data-retry-search': callbacks.onRetrySearchClicked,
            'data-retry': callbacks.onRetryClicked,
            'data-import': callbacks.onImportClicked
        };
        const resultsEl = this.#getResultsEl();
        resultsEl.addEventListener('click', evt => {
            for (const [attribute, callback] of Object.entries(clickActions)) {
                const button = evt.target.closest(`[${attribute}]`);
                if (!button)
                    continue;
                if (!button.disabled)
                    callback(button.getAttribute(attribute));
                return;
            }
        });
        resultsEl.addEventListener('change', evt => this.#onCheckboxChanged(evt, callbacks));
    }

    #onCheckboxChanged(evt, callbacks) {
        const analyteBox = evt.target.closest('[data-analyte]');
        if (analyteBox) {
            const injectionId = analyteBox.closest('[data-injection]').dataset.injection;
            callbacks.onAnalyteToggled(injectionId, analyteBox.dataset.analyte, analyteBox.checked);
            return;
        }
        const checkAll = evt.target.closest('[data-check-all]');
        if (checkAll)
            callbacks.onAllAnalytesToggled(checkAll.dataset.checkAll, checkAll.checked);
    }

    /**
     * @param {InjectionSearchRow} injection
     * @param {InjectionRowState} row
     */
    #rowTemplate(injection, row) {
        const idAttr = safeText(injection.id);
        if (injection.unavailable)
            return this.#unavailableRowTemplate(injection, idAttr);
        const analytesCount = row.analytes?.length ?? injection.analytesCount;
        const nothingSelected = row.selectedAnalyteIds?.size === 0;
        return `
            <div data-injection="${idAttr}">
                <div class="injection-search__row">
                    <button class="injection-search__expander" data-show-analytes="${idAttr}"
                            aria-expanded="${row.expanded}"
                            title="${row.expanded ? 'Hide' : 'Show'} analytes">
                        <i class="material-symbols-outlined injection-search__chevron">chevron_right</i>
                        <span class="injection-search__name">
                            <span class="injection-search__title-line">
                                <span class="injection-search__title">${safeText(injection.name)}</span>
                                ${analytesCount
                                    ? `<span class="injection-search__count">${analytesCount} analyte${analytesCount === 1 ? '' : 's'}</span>`
                                    : ''}
                            </span>
                        </span>
                    </button>
                    <div class="injection-search__actions">
                        <button class="injection-search__action injection-search__action--import"
                                data-import="${idAttr}"
                                ${row.importing ? 'data-importing disabled' : nothingSelected ? 'disabled' : ''}>
                            ${row.importing ? '<span class="injection-search__spinner" aria-hidden="true"></span>' : ''}
                            <span data-import-label>${row.importing ? 'Importing…' : this.#importLabel(row)}</span>
                        </button>
                    </div>
                </div>
                ${row.expanded ? this.#analytesTemplate(idAttr, row) : ''}
            </div>`;
    }

    /** The server didn't return an injection we remembered (deleted or no access). */
    #unavailableRowTemplate(injection, idAttr) {
        return `
            <div data-injection="${idAttr}">
                <div class="injection-search__row injection-search__row--unavailable">
                    <span class="injection-search__name">
                        <span class="injection-search__title">${safeText(injection.name)}</span>
                        <span class="injection-search__meta">No longer available</span>
                    </span>
                    <div class="injection-search__actions">
                        <button class="injection-search__action" data-forget="${idAttr}">Remove from recents</button>
                    </div>
                </div>
            </div>`;
    }

    /**
     * @param {string} idAttr - id of the injection
     * @param {InjectionRowState} row
     */
    #analytesTemplate(idAttr, row) {
        const panel = content => `<div class="injection-search__analytes">${content}</div>`;
        if (row.loading)
            return panel(`<div class="injection-search__no-analytes" role="status">Loading analytes…</div>`);
        if (row.error)
            return panel(`
                <div class="injection-search__analytes-error" role="status">
                    ${safeText(row.error)}
                    <button class="injection-search__forget" data-retry="${idAttr}">retry</button>
                </div>`);
        const analytes = row.analytes || [];
        if (!analytes.length)
            return panel(`<div class="injection-search__no-analytes" role="status">No analytes</div>`);
        const selected = row.selectedAnalyteIds;
        return panel(`
                <table class="injection-search__analyte-table">
                    <thead><tr>
                        <th class="injection-search__check">
                            <input type="checkbox" data-check-all="${idAttr}" aria-label="Select all analytes"
                                   ${selected.size === analytes.length ? 'checked' : ''}>
                        </th>
                        <th>Structure</th><th>Alias</th><th>MF</th>
                        <th class="injection-search__number">EMW</th>
                    </tr></thead>
                    <tbody>
                        ${analytes.map(a => `
                            <tr>
                                <td class="injection-search__check">
                                    <input type="checkbox" ${selected.has(a.id) ? 'checked' : ''}
                                           data-analyte="${safeText(a.id)}" aria-label="Import ${safeText(a.alias)}">
                                </td>
                                <td>${this.#structureTemplate(a)}</td>
                                <td>${safeText(a.alias)}</td>
                                <td>${a.mf ? safeText(a.mf) : '-'}</td>
                                <td class="injection-search__number">${a.emw ? a.emw.toFixed(4) : '-'}</td>
                            </tr>`).join('')}
                    </tbody>
                </table>`);
    }

    #structureTemplate(analyte) {
        if (!analyte.structureSvgPath)
            return `<div class="injection-search__no-structure">no structure</div>`;
        if (!analyte.structureSvg)
            return `<div class="injection-search__structure"></div>`;
        return `<div class="injection-search__structure">${analyte.structureSvg}</div>`;
    }

    /** @param {InjectionRowState} row */
    #importLabel(row) {
        const selected = row.selectedAnalyteIds;
        return !selected || selected.size === row.analytes?.length ? 'Import analytes' : `Import ${selected.size}`;
    }

    /** @param {InjectionRowState} row */
    #isPartlySelected(row) {
        const selected = row?.selectedAnalyteIds;
        return !!selected && !!row.analytes && selected.size > 0 && selected.size < row.analytes.length;
    }

    /** @return {HTMLInputElement} */
    #getSearchInputEl() {
        return this.#dialog.getElement('.injection-search__input');
    }

    /** @return {HTMLElement} */
    #getResultsEl() {
        return this.#dialog.getElement('.injection-search__results');
    }

    /** @return {HTMLElement}*/
    #getRowEl(injectionId) {
        return this.#getResultsEl().querySelector(`[data-injection="${CSS.escape(injectionId)}"]`);
    }
}
